import { Box, CopyIcon, IconButton, Surface, Text } from '@/ui/primitives';
import {
  buildCodeOutputBlockContentSx,
  buildCodeOutputBlockCopyButtonSx,
  buildCodeOutputBlockFrameSx,
  buildCodeOutputBlockHeaderSx,
  CODE_OUTPUT_BLOCK_LABEL_SX,
} from './CodeOutputBlock.styles';
import type { SxProps, Theme } from '@/ui/primitives/types';
import type { ReactElement, ReactNode } from 'react';

export type CodeOutputBlockProps = {
  value: string;
  label?: ReactNode;
  onCopy?: () => void;
  copyAriaLabel?: string;
  copyIconAlignment?: 'top' | 'center';
  reserveCopySpace?: boolean;
  background?: 'surface' | 'transparent';
  sx?: SxProps<Theme>;
  contentSx?: SxProps<Theme>;
};

export const CodeOutputBlock = ({
  value,
  label,
  onCopy,
  copyAriaLabel = 'Copy output',
  copyIconAlignment = 'top',
  reserveCopySpace = true,
  background = 'surface',
  sx,
  contentSx,
}: CodeOutputBlockProps): ReactElement => {
  const hasHeaderLabel = Boolean(label);
  const hasCopyAction = Boolean(onCopy);
  const showHeaderRow = hasHeaderLabel;
  const resolvedContentSx = buildCodeOutputBlockContentSx({
    showHeaderRow,
    hasCopyAction,
    reserveCopySpace,
    contentSx,
  });

  return (
    <Surface padding="none" border background={background} sx={sx}>
      <Box sx={buildCodeOutputBlockFrameSx({ showHeaderRow })}>
        {showHeaderRow ? (
          <Box sx={buildCodeOutputBlockHeaderSx({ hasCopyAction })}>
            {label ? (
              <Text role="label" color="textSecondary" sx={CODE_OUTPUT_BLOCK_LABEL_SX}>
                {label}
              </Text>
            ) : null}
          </Box>
        ) : null}
        {hasCopyAction ? (
          <IconButton
            size="medium"
            aria-label={copyAriaLabel}
            onClick={onCopy}
            sx={buildCodeOutputBlockCopyButtonSx({ copyIconAlignment })}
          >
            <CopyIcon />
          </IconButton>
        ) : null}
        <Text role="body" dataGridTypography="custom" sx={resolvedContentSx}>
          {value}
        </Text>
      </Box>
    </Surface>
  );
};
